import type {
  PlanningTableField,
  PlanningWorkspaceMetadata,
  WfRawField,
  WfRawRecordType,
  WfRawSection,
  WfRawWorkspaceMetadataResponse,
} from './erd-bridge.js';

/**
 * Mapping from Workfront Maestro raw shape → iframe-friendly shape.
 * Lifted from `wf-audit/src/slices/planning/data/planning.mapper.ts`
 * verbatim. The iframe app expects the canonical shape (fields with
 * resolved `sourceTableName`, `connectionType` etc.); we precompute it
 * SPA-side so the iframe stays stateless and the engine handler stays
 * a thin Maestro proxy.
 */
export function toField(
  field: WfRawField,
  internalRecordTypes: WfRawRecordType[],
): PlanningTableField {
  const isReference = field.type === 'reference';
  const ref = field.referenceOptions;
  const hasExternalSource = ref?.isExternal ?? false;

  const left = ref?.backField?.referenceOptions?.multiple ? 'M' : '1';
  const right = ref?.multiple ? 'M' : '1';

  let sourceTableName: string | null = null;
  let sourceFieldName: string | null = null;
  let connectionType: string | null = null;

  if (!hasExternalSource) {
    if (isReference) {
      sourceTableName =
        internalRecordTypes.find((rt) => rt.id === ref?.recordTypeId)?.displayName ?? null;
      sourceFieldName = field.linkedField?.displayName ?? null;
      connectionType = `${left}:${right}`;
    } else {
      const lookupSourceFieldId = field.lookupOptions?.sourceField?.id;
      sourceTableName = lookupSourceFieldId
        ? (internalRecordTypes.find((rt) => rt.fields?.some((f) => f.id === lookupSourceFieldId))
            ?.displayName ?? null)
        : null;
      sourceFieldName = field.lookupOptions?.sourceField?.displayName ?? null;
    }
  }

  return {
    id: field.id,
    name: field.displayName,
    type: field.type,
    hasExternalSource,
    externalOptions: ref?.externalOptions ?? null,
    sourceTableName,
    sourceFieldName,
    connectionType,
  };
}

export function toWorkspaceMetadata(
  raw: WfRawWorkspaceMetadataResponse,
): PlanningWorkspaceMetadata {
  const sections: WfRawSection[] = raw.recordTypeSections ?? [];
  const internalRecordTypes: WfRawRecordType[] = (raw.recordTypes ?? []).filter(
    (rt) => !rt.isExternal,
  );

  return {
    recordTypes: internalRecordTypes.map((rt) => ({
      id: rt.id,
      name: rt.displayName,
      section: sections.find((s) => s.recordTypeIds?.includes(rt.id))?.displayName ?? '',
      icon: rt.icon,
      color: rt.color,
      fields: (rt.fields ?? []).map((f) => toField(f, internalRecordTypes)),
    })),
    recordTypeSections: sections.map((s, i) => ({
      name: s.displayName,
      position: i + 1,
    })),
  };
}
